/*
 * Daro vehicle finder (snippets/daro-finder.liquid) and the product page fitment line (snippets/daro-fits.liquid).
 *
 * Index: one static JSON file (shop metafield daro.vehicle_index_url, scripts/fitment/vehicle-index.mjs):
 *   { s: [segment handles], y: { "2019": { "Aston Martin": { "DB11": { "AMR": ["2019-aston-martin-db11-amr", 2] } } } } }
 * Fetched once per session (sessionStorage), on idle or the first touch of a finder; the CDN and browser cache it.
 *
 * Segment pages only offer that segment's vehicles (window.DaroSegments from assets/daro-segments.js; outside a
 * segment, every vehicle a segment covers). Search goes to the vehicle page, /collections/<vehicle key>.
 *
 * The chosen vehicle, applied when Search is clicked, or as soon as all four are picked (theme setting "Apply the
 * vehicle", DaroVehicle.mode 'search' / 'pick'):
 * - localStorage "daroFinder" refills every finder on every page.
 * - window.DaroVehicle.set(key, name) (snippets/daro-vehicle-redirect.liquid) narrows brands, packages and search.
 * - Reset, or ('pick' only) a shopper's own change that leaves the finder incomplete: DaroVehicle.forget() (which
 *   also wipes "daroFinder"). Nothing is forgotten just because a finder renders empty. Switching segment
 *   (American / European / Exotic) clears it too (daro-vehicle-redirect, before this script reads it).
 * Every storage read and write is guarded: with storage blocked the finder still works for the page.
 */
(() => {
  const FINDER_KEY = 'daroFinder';
  const KEY_RE = /^[a-z0-9-]{3,120}$/;
  const LEVELS = ['year', 'make', 'model', 'submodel'];

  const readJson = (store, key) => {
    try {
      return JSON.parse(window[store].getItem(key) || 'null');
    } catch (e) {
      return null;
    }
  };
  const writeJson = (store, key, value) => {
    try {
      if (value == null) window[store].removeItem(key);
      else window[store].setItem(key, JSON.stringify(value));
    } catch (e) {}
  };

  // ---- The chosen vehicle ----
  // { key, name, year, make, model, submodel }
  const validVehicle = (v) => v && typeof v === 'object' && KEY_RE.test(v.key || '') && v.year && v.make && v.model;
  let chosen = readJson('localStorage', FINDER_KEY);
  if (!validVehicle(chosen)) chosen = null;
  // Recent-vehicle chips from an earlier build of the finder: no longer kept
  writeJson('localStorage', 'daroGarage', null);
  const nameOf = (v) => [v.year, v.make, v.model, v.submodel].filter(Boolean).join(' ');

  function choose(v) {
    chosen = v;
    writeJson('localStorage', FINDER_KEY, v);
    const dv = window.DaroVehicle;
    if (dv && dv.key !== v.key) dv.set(v.key, v.name);
    document.dispatchEvent(new CustomEvent('daro:vehicle', { detail: v }));
  }

  // 'search': Search applies the vehicle; 'pick': picking all four does (no DaroVehicle: remembered on pick)
  const applyOnPick = () => !window.DaroVehicle || window.DaroVehicle.mode === 'pick';

  function forget() {
    chosen = null;
    writeJson('localStorage', FINDER_KEY, null);
    const dv = window.DaroVehicle;
    if (dv) dv.forget();
    document.dispatchEvent(new CustomEvent('daro:vehicle', { detail: null }));
  }

  // DaroVehicle.forget() (the chips' "Show all", a gone vehicle page) wipes the finder's vehicle too
  const dv = window.DaroVehicle;
  if (dv && typeof dv.forget === 'function' && !dv.forget.daroFinder) {
    const original = dv.forget;
    dv.forget = function () {
      chosen = null;
      writeJson('localStorage', FINDER_KEY, null);
      document.querySelectorAll('[data-daro-finder]').forEach((form) => form.daroFinder && form.daroFinder.reset());
      return original.apply(this, arguments);
    };
    dv.forget.daroFinder = true;
  }
  // The remembered vehicle narrows this session too (the cookie only lasts the session)
  if (chosen && dv && dv.key !== chosen.key) dv.set(chosen.key, chosen.name || nameOf(chosen));

  // ---- Segment rules ----
  const segments = () => window.DaroSegments || null;
  function allowed(make, model, leaf) {
    const ds = segments();
    if (ds && typeof ds.modelAllowed === 'function') return ds.modelAllowed(make, model);
    return leaf[1] >= 0;
  }

  // ---- Index ----
  let indexPromise = null;
  function loadIndex(url) {
    if (indexPromise) return indexPromise;
    const cacheKey = `daroFinderIndex:${url}`;
    const cached = readJson('sessionStorage', cacheKey);
    if (cached && cached.y) {
      indexPromise = Promise.resolve(cached);
      return indexPromise;
    }
    indexPromise = fetch(url, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.y) throw new Error('vehicle index unreadable');
        try {
          // Older indexes from earlier pages of the session are dropped first
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith('daroFinderIndex:') && k !== cacheKey) sessionStorage.removeItem(k);
          }
        } catch (e) {}
        writeJson('sessionStorage', cacheKey, data);
        return data;
      })
      .catch((e) => {
        indexPromise = null;
        throw e;
      });
    return indexPromise;
  }

  // Index -> only the vehicles this page offers: year -> make -> model -> submodel -> leaf
  function filtered(index) {
    const out = new Map();
    const years = Object.keys(index.y).sort((a, b) => b - a);
    for (const year of years) {
      const makes = new Map();
      for (const [make, models] of Object.entries(index.y[year])) {
        const ms = new Map();
        for (const [model, subs] of Object.entries(models)) {
          const ss = new Map();
          for (const [sub, leaf] of Object.entries(subs)) if (allowed(make, model, leaf)) ss.set(sub, leaf);
          if (ss.size) ms.set(model, ss);
        }
        if (ms.size) makes.set(make, ms);
      }
      if (makes.size) out.set(year, makes);
    }
    return out;
  }

  const collectionsUrl = (form, key) => `${(form.getAttribute('action') || '/collections').replace(/\/$/, '')}/${encodeURIComponent(key)}`;

  // ---- One finder ----
  function setup(form) {
    if (form.daroFinder) return;
    const selects = {};
    LEVELS.forEach((level) => (selects[level] = form.querySelector(`select[data-level="${level}"]`)));
    if (LEVELS.some((l) => !selects[l])) return;
    const search = form.querySelector('.daro-finder__search');
    const clearBtn = form.querySelector('[data-finder-clear]');
    const url = form.getAttribute('data-index');
    let tree = null; // filtered index
    let leaf = null; // [key, segment] of the complete selection

    const placeholder = (select) => select.options[0];
    function fill(select, values, value) {
      const first = placeholder(select);
      select.textContent = '';
      select.appendChild(first);
      for (const v of values) {
        const opt = document.createElement('option');
        opt.value = v;
        opt.textContent = v;
        select.appendChild(opt);
      }
      select.value = value && values.includes(value) ? value : '';
      select.disabled = !values.length;
    }
    function clearBelow(level) {
      LEVELS.slice(LEVELS.indexOf(level) + 1).forEach((l) => {
        const s = selects[l];
        const first = placeholder(s);
        s.textContent = '';
        s.appendChild(first);
        s.value = '';
        s.disabled = true;
      });
    }
    const values = () => Object.fromEntries(LEVELS.map((l) => [l, selects[l].value]));
    function node(level, v) {
      // The map of options for this level, given the values above it
      let n = tree;
      for (const l of LEVELS.slice(0, LEVELS.indexOf(level))) {
        n = n && n.get(v[l]);
      }
      return n || null;
    }
    const byName = (a, b) => a.localeCompare(b, 'en', { numeric: true });
    function options(level, v) {
      const n = node(level, v);
      if (!n) return [];
      const list = [...n.keys()];
      return level === 'year' ? list : list.sort(byName);
    }
    function sync() {
      const v = values();
      const n = tree && v.submodel ? node('submodel', v) : null;
      leaf = n && n.get(v.submodel) ? n.get(v.submodel) : null;
      search.disabled = !leaf;
      return v;
    }

    // Before the index arrives: show the remembered vehicle as single options, so the finder reads right at once
    function showRemembered() {
      if (!chosen) return;
      if (otherSegment(chosen)) return;
      LEVELS.forEach((l) => {
        if (chosen[l]) fill(selects[l], [chosen[l]], chosen[l]);
      });
      if (chosen.submodel) {
        leaf = [chosen.key, -1];
        search.disabled = false;
      }
    }
    function restore() {
      // With the index: every level's options, the remembered vehicle selected where this page offers it
      fill(selects.year, options('year', {}), chosen && !otherSegment(chosen) ? chosen.year : '');
      LEVELS.slice(1).forEach((l, i) => {
        const above = LEVELS[i];
        if (selects[above].value) fill(selects[l], options(l, values()), chosen && selects[above].value === chosen[above] ? chosen[l] : '');
        else clearBelow(above);
      });
      sync();
    }

    // A remembered vehicle from another segment isn't offered here: the finder starts empty
    function otherSegment(v) {
      const ds = segments();
      if (!ds || !ds.current || typeof ds.segmentFor !== 'function') return null;
      const owner = ds.segmentFor(v.make, v.model);
      return owner && owner.handle !== ds.current ? owner : null;
    }
    let ready = null;
    function ensureIndex() {
      if (ready) return ready;
      if (!url) {
        ready = Promise.reject(new Error('no vehicle index'));
        return ready;
      }
      form.classList.add('is-loading');
      const yearPlaceholder = placeholder(selects.year);
      const label = yearPlaceholder.textContent;
      if (!tree && selects.year.options.length < 2) yearPlaceholder.textContent = 'Loading…';
      ready = loadIndex(url)
        .then((index) => {
          tree = filtered(index);
          restore();
        })
        .catch(() => {
          ready = null;
          form.classList.add('is-error');
        })
        .finally(() => {
          yearPlaceholder.textContent = label;
          form.classList.remove('is-loading');
        });
      return ready;
    }

    // A shopper's own change (not one this script makes, like taking a model's only trim)
    let byShopper = false;
    form.addEventListener('change', (event) => {
      const level = event.target && event.target.getAttribute('data-level');
      if (!level || !tree) return;
      const outer = event.isTrusted;
      if (outer) byShopper = true;
      try {
        onChange(level);
      } finally {
        if (outer) byShopper = false;
      }
    });
    function onChange(level) {
      clearBelow(level);
      const v = values();
      const next = LEVELS[LEVELS.indexOf(level) + 1];
      if (next && v[level]) {
        const opts = options(next, v);
        fill(selects[next], opts, '');
        // One choice only (a model with a single trim): take it
        if (opts.length === 1) {
          selects[next].value = opts[0];
          selects[next].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
      const after = sync();
      if (!applyOnPick()) return; // applied by the Search click
      if (leaf) choose(vehicleOf(after));
      else if (byShopper && chosen) forget();
    }
    // Load the index the moment the shopper reaches for the finder (before the select opens, where possible)
    ['pointerdown', 'focusin', 'keydown', 'touchstart'].forEach((type) => form.addEventListener(type, ensureIndex, { passive: true, once: true }));

    const vehicleOf = (v) => ({ key: leaf[0], name: nameOf(v), year: v.year, make: v.make, model: v.model, submodel: v.submodel });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!leaf) {
        ensureIndex();
        return;
      }
      choose(vehicleOf(values()));
      window.location.href = collectionsUrl(form, leaf[0]);
    });
    if (clearBtn)
      clearBtn.addEventListener('click', () => {
        forget(); // DaroVehicle.forget() resets every finder on the page (form.daroFinder.reset)
        form.daroFinder.reset();
      });

    form.daroFinder = {
      reset() {
        clearBelow('year');
        selects.year.value = '';
        if (tree) fill(selects.year, options('year', {}), '');
        leaf = null;
        search.disabled = true;
      },
      ensureIndex,
    };
    showRemembered();
    return form;
  }

  // ---- Product page: "Fits your <vehicle>" / "Check fitment" ----
  function renderFits(el) {
    const keys = (el.getAttribute('data-keys') || '').split('|').filter(Boolean);
    const vehicle = chosen;
    const text = el.querySelector('[data-fits-text]');
    const link = el.querySelector('[data-fits-link]');
    if (!text) return;
    el.classList.remove('is-fit', 'is-unknown', 'is-nofit');
    if (!keys.length) {
      el.hidden = true;
      return;
    }
    // No vehicle picked: show nothing rather than a "pick your vehicle" prompt
    el.hidden = !vehicle;
    if (!vehicle) return;
    if (keys.includes(vehicle.key)) {
      el.classList.add('is-fit');
      text.textContent = `Fits your ${vehicle.name || nameOf(vehicle)}`;
      if (link) {
        link.textContent = 'More parts for it';
        link.href = `/collections/${vehicle.key}`;
      }
    } else {
      el.classList.add('is-nofit');
      text.textContent = `Not listed for your ${vehicle.name || nameOf(vehicle)}. Check fitment`;
      if (link) {
        link.textContent = 'Parts that fit it';
        link.href = `/collections/${vehicle.key}`;
      }
    }
  }
  const renderAllFits = () => document.querySelectorAll('[data-daro-fits]').forEach(renderFits);
  document.addEventListener('daro:vehicle', renderAllFits);

  function init() {
    const forms = Array.from(document.querySelectorAll('[data-daro-finder]'));
    forms.forEach(setup);
    renderAllFits();
    if (!forms.length) return;
    // Already fetched on an earlier page of this session: build the lists straight away
    forms.forEach((f) => {
      const url = f.getAttribute('data-index');
      let cached = false;
      try {
        cached = Boolean(url && sessionStorage.getItem(`daroFinderIndex:${url}`));
      } catch (e) {}
      if (cached && f.daroFinder) f.daroFinder.ensureIndex();
    });
    // A remembered vehicle needs the full lists soon; otherwise wait until the page is idle
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const later = () => idle(() => forms.forEach((f) => f.daroFinder && f.daroFinder.ensureIndex()), { timeout: 4000 });
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  }

  window.DaroFinder = { get vehicle() { return chosen; }, forget };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
